import {
	Component,
	EventEmitter,
	Input,
	Output,
	OnDestroy,
	ChangeDetectionStrategy,
	ElementRef,
	inject
} from '@angular/core';
import { Subject, Subscription, filter, take, takeUntil } from 'rxjs';
import { LibraryTreeDataSource } from './library-tree-datasource';
import { TranslatePipe } from '@ngx-translate/core';

import {
	LibraryCollection,
	LibraryCollectionItemsAndFolders
} from '@go-modules/interfaces/library/library-collection';
import { ContentTypeType } from '@go-modules/interfaces/library/library-types';
import {
	CONTENT_TYPES,
	MODES,
	SORT_OPTIONS
} from '@go-modules/components/library/library-collections-viewer/library-collection-viewer.constants';
import { MatTreeModule } from '@angular/material/tree';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { FormsModule } from '@angular/forms';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatButtonModule } from '@angular/material/button';
import { NgxMediaThumbnailModule } from '@go-modules/components/media-thumbnail/media-thumbnail.module';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NgxZeroStateModule } from '@go-modules/components/zero-state/zero-state.module';
import {
	CollectionFolderViewerModule
} from '@go-modules/components/library/collection-folder-viewer/collection-folder-viewer.module';
import {
	CollectionItemViewerModule
} from '@go-modules/components/library/collection-item-viewer/collection-item-viewer.module';
import { LibraryTreeNode, NodeType } from '@go-modules/components/library/library-tree-viewer/library-tree-node';
import { OrgSettings } from '@go-modules/interfaces/groups/org-settings';

@Component({
	selector: 'ngx-library-tree-viewer',
	templateUrl: './library-tree-viewer.component.html',
	styleUrls: ['./library-tree-viewer.component.scss'],
	imports: [
		MatTreeModule,
		MatMenuModule,
		MatIconModule,
		MatCheckboxModule,
		FormsModule,
		MatProgressBarModule,
		TranslatePipe,
		MatButtonModule,
		NgxMediaThumbnailModule,
		MatProgressSpinnerModule,
		NgxZeroStateModule,
		CollectionFolderViewerModule,
		CollectionItemViewerModule
	],
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class LibraryTreeViewerComponent implements OnDestroy {
	@Input() public collectionItems: LibraryCollectionItemsAndFolders;
	@Input() public selectedCollection: LibraryCollection;
	@Input() public selectedContentType: ContentTypeType;
	@Input() public collections: LibraryCollection[];
	@Input() public archiveCollection: LibraryCollection;
	@Input() public mode: string;
	@Input() public multiSelect: boolean = false;
	@Input() public sortBy: string = SORT_OPTIONS.LASTMODIFIED;
	@Input() public searchStr: string = '';
	@Input() public orgSettings: OrgSettings;
	@Input() public expandedHeight: number = 370;
	@Input() public currentlySelected: { id?: number; hash?: string; folderId?: number; };

	@Output() public selectItem = new EventEmitter<any>();
	@Output() public setCurrentlySelected = new EventEmitter<any>();
	@Output() public loadNewItems = new EventEmitter<any>();
	@Output() public setActivityPanelIsOpen = new EventEmitter<boolean>();
	@Output() public setSelectAllChecked = new EventEmitter<boolean>();

	public MODES = MODES;
	public CONTENT_TYPES = CONTENT_TYPES;
	private destroy$ = new Subject<void>();
	private pendingFocusSubscriptions = new Map<string, Subscription>();
	public dataSource = inject(LibraryTreeDataSource);
	private elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

	public ngOnDestroy (): void {
		this.pendingFocusSubscriptions.forEach((subscription) => subscription.unsubscribe());
		this.pendingFocusSubscriptions.clear();
		this.destroy$.next();
		this.destroy$.complete();
		this.dataSource.disconnect();
	}

	public handleExpandedChange (node: LibraryTreeNode, expanded: boolean): void {
		if (expanded && !this.dataSource.isExpanded(node)) {
			this.dataSource.expand(node);
		} else if (!expanded && this.dataSource.isExpanded(node)) {
			this.dataSource.collapse(node);
		}
	}

	public isFolderEmpty (node: LibraryTreeNode): boolean {
		if (node.type !== 'folder' || node.isLoading) {
			return false;
		}

		if (!this.dataSource.isExpanded(node)) {
			return false;
		}

		const nodeIndex = this.dataSource.data.indexOf(node);
		if (nodeIndex === -1 || nodeIndex === this.dataSource.data.length - 1) {
			return true; // Last node or not found
		}

		// Check if next node is a child (has higher level)
		const nextNode = this.dataSource.data[nodeIndex + 1];
		return !nextNode || nextNode.level <= node.level;
	}

	public trackNode = (_index: number, node: LibraryTreeNode): string => {
		return node.key;
	};

	public handleTreeKeydown (event: KeyboardEvent): void {
		const target = event.target as HTMLElement | null;
		if (!target) return;

		const nodeElement = target.closest<HTMLElement>('[data-tree-node-key]');
		if (!nodeElement) return;

		const nodeKey = nodeElement.dataset.treeNodeKey;
		if (!nodeKey) return;

		const nodeIndex = this.dataSource.data.findIndex((node) => node.key === nodeKey);
		if (nodeIndex < 0) return;

		const node = this.dataSource.data[nodeIndex];

		switch (event.key) {
			case 'ArrowDown':
				event.preventDefault();
				this.focusNodeByIndex(nodeIndex + 1);
				break;
			case 'ArrowUp':
				event.preventDefault();
				this.focusNodeByIndex(nodeIndex - 1);
				break;
			case 'ArrowRight':
				event.preventDefault();
				this.handleArrowRight(node, nodeIndex);
				break;
			case 'ArrowLeft':
				event.preventDefault();
				this.handleArrowLeft(node, nodeIndex);
				break;
			case 'Enter':
				event.preventDefault();
				nodeElement.click();
				break;
			case ' ':
			case 'Space':
			case 'Spacebar':
				event.preventDefault();
				nodeElement.click();
				break;
			case 'Home':
			case 'End':
			case '*':
				event.preventDefault();
				break;
			default:
				break;
		}
	}

	private handleArrowRight (node: LibraryTreeNode, _nodeIndex: number): void {
		if (node.type !== NodeType.Folder) return;

		if (!this.dataSource.isExpanded(node)) {
			this.dataSource.expand(node);
			return;
		}

		this.focusFirstChildAfterExpand(node);
	}

	private handleArrowLeft (node: LibraryTreeNode, _nodeIndex: number): void {
		if (node.type === NodeType.Folder && this.dataSource.isExpanded(node)) {
			this.dataSource.collapse(node);
			return;
		}

		if (node.level === 0) return;

		const parentIndex = this.findParentIndex(_nodeIndex);
		if (parentIndex >= 0) {
			this.focusNodeByIndex(parentIndex);
		}
	}

	private findParentIndex (childIndex: number): number {
		const child = this.dataSource.data[childIndex];
		if (!child || child.level === 0) return -1;

		for (let i = childIndex - 1; i >= 0; i--) {
			if (this.dataSource.data[i].level === child.level - 1) {
				return i;
			}
		}

		return -1;
	}

	private focusNodeByIndex (nodeIndex: number): void {
		if (nodeIndex < 0 || nodeIndex >= this.dataSource.data.length) return;
		this.focusNodeByKey(this.dataSource.data[nodeIndex].key);
	}

	private focusNodeByKey (nodeKey: string): void {
		const nodeElement = this.findNodeElement(nodeKey);
		if (nodeElement) {
			nodeElement.focus();
		}
	}

	private focusFirstChildAfterExpand (node: LibraryTreeNode): void {
		const existingChildKey = this.getFirstChildKey(node);
		if (existingChildKey) {
			this.focusNodeByKey(existingChildKey);
			return;
		}

		this.pendingFocusSubscriptions.get(node.key)?.unsubscribe();

		const subscription = this.dataSource.data$.pipe(
			filter(() => Boolean(this.getFirstChildKey(node))),
			take(1),
			takeUntil(this.destroy$)
		).subscribe(() => {
			const childKey = this.getFirstChildKey(node);
			if (childKey) {
				this.focusNodeByKey(childKey);
			}
			this.pendingFocusSubscriptions.delete(node.key);
		});

		this.pendingFocusSubscriptions.set(node.key, subscription);
	}

	private getFirstChildKey (node: LibraryTreeNode): string | null {
		const currentIndex = this.dataSource.data.findIndex((dataNode) => dataNode.key === node.key);
		if (currentIndex < 0) return null;

		const nextNode = this.dataSource.data[currentIndex + 1];
		if (nextNode && nextNode.level > node.level) {
			return nextNode.key;
		}

		return null;
	}

	private findNodeElement (nodeKey: string): HTMLElement | null {
		const elements = this.elementRef.nativeElement
			.querySelectorAll<HTMLElement>('[data-tree-node-key]');

		for (const element of Array.from(elements)) {
			if (element.dataset.treeNodeKey === nodeKey) {
				return element;
			}
		}

		return null;
	}

	public handleSelectItem (event: any): void {
		this.selectItem.emit(event);
	}
}
